import { Box, Group, Progress, Stack, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { scoreColor } from '@/design/ScoreColors';
import InsightsScoreRing from './InsightsScoreRing';
import type { AdoptionBreakdown } from './InsightsTypes';

interface DriverMeta {
    key: keyof Omit<AdoptionBreakdown, 'score'>;
    label: string;
    hint: string;
}

/** The four adoption drivers, combined as a simple average into the composite score. */
const DRIVERS: DriverMeta[] = [
    { key: 'frequency', label: 'Frequency', hint: 'Active days per week (of 5)' },
    { key: 'consistency', label: 'Consistency', hint: 'Share of weeks active — the habit' },
    { key: 'complexity', label: 'Complexity', hint: 'Low / Medium / High task mix' },
    { key: 'volume', label: 'Activity', hint: 'Meaningful throughput per active week' },
];

/** Adoption — the composite adoption score and the four weighted drivers behind it. */
export default function InsightsAdoptionSection({ adoption }: { adoption: AdoptionBreakdown }): ReactElement {
    return (
        <SurfaceCard $padding="lg">
            <Group gap="xl" align="center" wrap="nowrap">
                <InsightsScoreRing score={adoption.score} label="Adoption" color={scoreColor(adoption.score)} />
                <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
                    {DRIVERS.map((d) => (
                        <Box key={d.key}>
                            <Group justify="space-between" wrap="nowrap" mb={4}>
                                <Text size="sm" fw={600}>
                                    {d.label}
                                </Text>
                                <Text size="sm" fw={700} ff="monospace" style={{ color: scoreColor(adoption[d.key]) }}>
                                    {adoption[d.key]}
                                </Text>
                            </Group>
                            <Progress value={adoption[d.key]} color={scoreColor(adoption[d.key])} size="sm" radius="xl" />
                            <Text size="xs" c="dimmed" mt={4}>
                                {d.hint}
                            </Text>
                        </Box>
                    ))}
                </Stack>
            </Group>
        </SurfaceCard>
    );
}
